import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSendEsimInstructions } from '@/hooks/useLineActions';
import { createLogger } from '@/lib/logger';
import { Loader2, Mail } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

const log = createLogger('SendEsimInstructionsDialog');

interface SendEsimInstructionsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  lineId: string;
  defaultEmail?: string;
}

const emailSchema = z.string().email('Adresse email invalide');

export function SendEsimInstructionsDialog({
  isOpen,
  onClose,
  lineId,
  defaultEmail = '',
}: SendEsimInstructionsDialogProps) {
  const [email, setEmail] = useState(defaultEmail || '');
  const [error, setError] = useState<string | null>(null);
  const { mutate: sendEsimInstructions, isPending } = useSendEsimInstructions();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    const validationResult = emailSchema.safeParse(email);
    if (!validationResult.success) {
      setError(validationResult.error.errors[0].message);
      return;
    }

    // Envoyer l'email
    sendEsimInstructions(
      { lineId, recipientEmail: email },
      {
        onSuccess: () => {
          setEmail('');
          onClose();
        },
        onError: (err) => {
          log.error('Error sending eSIM instructions', err);
          setError(err.message || 'Erreur lors de l\'envoi de l\'email');
        },
      }
    );
  };

  const handleClose = () => {
    if (!isPending) {
      setEmail(defaultEmail);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Envoyer les instructions eSIM par email
          </DialogTitle>
          <DialogDescription>
            Entrez l'adresse email du destinataire pour recevoir les instructions
            d'activation de l'eSIM avec le code QR.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Adresse email</Label>
              <Input
                id="email"
                type="email"
                placeholder="exemple@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                disabled={isPending}
                aria-invalid={error ? 'true' : 'false'}
              />
              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isPending}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={isPending || !email || !email.trim()}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Envoyer
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

